import {
  AddContactAsIamUserForCompanyRequest,
  AddContactAsIamUserForCompanyStatus,
  CreateAccountGroupForCompanyRequest,
  CreateAccountGroupForCompanyStatus,
  GetCreateAccountGroupForCompanyStatusRequest,
  PlatformApiService,
  RemoveContactAsIamUserForCompanyRequest,
} from '@vendasta/crm-integrations';
import { Inject, Injectable } from '@angular/core';
import { CrmDependencies, CrmInjectionToken } from '../tokens-and-interfaces';
import { Observable, of } from 'rxjs';
import { catchError, map, switchMap, take } from 'rxjs/operators';
import { HttpErrorResponse } from '@angular/common/http';
import { IsContactIamUserForCompanyRequest } from '@vendasta/crm-integrations';

export interface CompanyAccountGroupStatus {
  status: CreateAccountGroupForCompanyStatus;
  accountGroupId: string;
}

@Injectable()
export class CrmPlatformApiService {
  constructor(
    @Inject(CrmInjectionToken) private readonly config: CrmDependencies,
    private platformAPIService: PlatformApiService,
  ) {}

  createAccountGroupForCompany(companyId: string): Observable<boolean> {
    return this.config.namespace$.pipe(
      switchMap((namespace) => {
        const request = {
          namespace: namespace,
          companyId: companyId,
        } as CreateAccountGroupForCompanyRequest;
        return this.platformAPIService.createAccountGroupForCompany(request);
      }),
      map((response) => response.ok),
      take(1),
    );
  }

  getCreateAccountGroupForCompanyStatus(companyId: string): Observable<CompanyAccountGroupStatus> {
    return this.config.namespace$.pipe(
      switchMap((namespace) => {
        const request = {
          namespace: namespace,
          companyId: companyId,
        } as GetCreateAccountGroupForCompanyStatusRequest;
        return this.platformAPIService.getCreateAccountGroupForCompanyStatus(request);
      }),
      map((response) => {
        return { status: response.status, accountGroupId: response.accountGroupId };
      }),
      take(1),
    );
  }

  getAccountGroupForCompany(companyId: string): Observable<string> {
    return this.config.namespace$.pipe(
      switchMap((namespace) => {
        const request = {
          namespace: namespace,
          companyId: companyId,
        } as GetCreateAccountGroupForCompanyStatusRequest;
        return this.platformAPIService.getAccountGroupForCompany(request);
      }),
      map((response) => response.accountGroupId),
      take(1),
    );
  }

  addContactAsIamUserForCompany(contactId: string, companyId: string): Observable<AddContactAsIamUserForCompanyStatus> {
    return this.config.namespace$.pipe(
      switchMap((namespace) => {
        const request = {
          namespace: namespace,
          contactId: contactId,
          companyId: companyId,
        } as AddContactAsIamUserForCompanyRequest;
        return this.platformAPIService.addContactAsIamUserForCompany(request);
      }),
      catchError((error) => {
        return of(error);
      }),
      map((response) => {
        if (response instanceof HttpErrorResponse) {
          return null;
        }
        return response.status;
      }),
      take(1),
    );
  }

  removeContactAsIamUserForCompany(contactId: string, companyId: string): Observable<boolean> {
    return this.config.namespace$.pipe(
      switchMap((namespace) => {
        const request = {
          namespace: namespace,
          contactId: contactId,
          companyId: companyId,
        } as RemoveContactAsIamUserForCompanyRequest;
        return this.platformAPIService.removeContactAsIamUserForCompany(request);
      }),
      map((response) => response.ok),
      take(1),
    );
  }

  isContactIamUserForCompany(contactId: string, companyId: string): Observable<boolean> {
    return this.config.namespace$.pipe(
      switchMap((namespace) => {
        const request = {
          namespace: namespace,
          contactId: contactId,
          companyId: companyId,
        } as IsContactIamUserForCompanyRequest;
        return this.platformAPIService.isContactIamUserForCompany(request);
      }),
      map((response) => {
        return !!response.isIamUser;
      }),
      take(1),
    );
  }
}
